import { useEffect } from 'react'

// The scribe rail is `position: fixed`, so the chart layout reserves matching
// space via the global CSS var `--zoom-panel-width` (Shell + PatientChartLayout
// pad/shrink by it). Several rail components publish it (ZoomPanel,
// InterruptedResumePanel), so a rail that unmounts must NOT clear the var while
// another rail still needs it — ref-count the active reservers and clear only
// when none remain.
const reservers = new Map<symbol, number>()

function apply(): void {
  if (reservers.size === 0) {
    document.documentElement.style.removeProperty('--zoom-panel-width')
    return
  }
  document.documentElement.style.setProperty(
    '--zoom-panel-width',
    `${Math.max(...reservers.values())}px`,
  )
}

/**
 * Reserve `--zoom-panel-width` while a rail occupies the fixed right slot. Pass
 * the rail's width to reserve, or `null` to release. Ref-counted across all
 * callers so one rail unmounting never wipes a reservation another still needs.
 */
export function useZoomPanelWidthReservation(width: number | null): void {
  useEffect(() => {
    if (width == null) return
    const token = Symbol('zoom-panel-reserver')
    reservers.set(token, width)
    apply()
    return () => {
      reservers.delete(token)
      apply()
    }
  }, [width])
}
